import { useEffect, useCallback, useRef, useMemo, useState, type RefObject } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type * as mapboxgl from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import stormTrackingService, { type StormTrackingData } from '../../api/stormTrackingService';

dayjs.extend(utc);

interface UseStormTrackingLayerParams {
  mapRef: RefObject<mapboxgl.Map | null>;
  selectedEvent: EventWithCircuit | null;
  showStormTracking: boolean;
  featureEnabled: boolean;
  selectedDateRange: { start: Date; end: Date } | null;
  currentTimestampUtc: string | null;
  styleVersion: number;
  onStormTrackingUpdate?: (data: StormTrackingData | null) => void;
}

const SWATH_FILL_LAYER_ID = 'storm-track-swath-fill';
const SWATH_OUTLINE_LAYER_ID = 'storm-track-swath-outline';
const LINE_LAYER_ID = 'storm-track-line';
const POINTS_LAYER_ID = 'storm-track-points';
const LABELS_LAYER_ID = 'storm-track-labels';
const SOURCE_ID = 'storm-track-source';

const ALL_LAYER_IDS = [
  LABELS_LAYER_ID, POINTS_LAYER_ID, LINE_LAYER_ID,
  SWATH_OUTLINE_LAYER_ID, SWATH_FILL_LAYER_ID,
];

function makeCirclePolygon(
  lat: number,
  lon: number,
  radiusKm: number,
  nPoints = 32,
): [number, number][] {
  const coords: [number, number][] = [];
  const cosLat = Math.max(Math.cos(lat * Math.PI / 180), 1e-6);
  for (let i = 0; i <= nPoints; i++) {
    const angle = (i / nPoints) * 2 * Math.PI;
    const dLat = (radiusKm / 111.0) * Math.cos(angle);
    const dLon = (radiusKm / (111.0 * cosLat)) * Math.sin(angle);
    coords.push([lon + dLon, lat + dLat]);
  }
  return coords;
}

function labelOffset(directionDeg: number | null): [number, number] {
  if (directionDeg == null) return [0, -2.2];
  const toRad = (directionDeg + 180) * Math.PI / 180;
  const ox = -Math.sin(toRad) * 2.5;
  const oy = Math.cos(toRad) * 2.5;
  return [ox, Math.min(oy, -1.5)];
}

/**
 * Storm tracking — "storm forecast badge" visualization.
 *
 * Renders the selected storm cell as a prominent marker with projected
 * center positions at +15/+30/+45/+60 min, a translucent uncertainty
 * swath around the projected path, and ETA/intensity/speed labels.
 *
 * Visually distinct from Rain Path's long red time-ruler line.
 */
export function useStormTrackingLayer({
  mapRef,
  selectedEvent,
  showStormTracking,
  featureEnabled,
  selectedDateRange,
  currentTimestampUtc,
  styleVersion,
  onStormTrackingUpdate,
}: UseStormTrackingLayerParams): void {
  const [stormTrackingRecords, setStormTrackingRecords] = useState<StormTrackingData[]>([]);
  const lastFetchedRangeRef = useRef<string | null>(null);

  const isEnabled = featureEnabled && showStormTracking && !!selectedEvent;

  const isMapStyleLoaded = useCallback((map: mapboxgl.Map): boolean => {
    try {
      const style = map.getStyle?.();
      if (!style?.version) return false;
      return map.isStyleLoaded();
    } catch {
      return false;
    }
  }, []);

  const cleanupLayers = useCallback((map: mapboxgl.Map) => {
    try {
      if (!isMapStyleLoaded(map)) return;
      ALL_LAYER_IDS.forEach(id => {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* */ }
      });
      try { if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID); } catch { /* */ }
    } catch { /* */ }
  }, [isMapStyleLoaded]);

  const buildGeoJSON = useCallback((
    stormData: StormTrackingData,
  ): GeoJSON.FeatureCollection => {
    const features: GeoJSON.Feature[] = [];
    const { track_points } = stormData;

    if (!track_points || track_points.length === 0) {
      return { type: 'FeatureCollection', features };
    }

    // Swath: outline + soft fill at each projected position using
    // backend-provided uncertainty_radius_km (semantic, not guessed)
    track_points.forEach((tp) => {
      const radius = Math.max(tp.uncertainty_radius_km ?? 4, 4);
      const isCurrent = tp.minutes_ahead === 0;
      const fillOpacity = isCurrent ? 0.10 : Math.max(0.02, 0.06 - tp.minutes_ahead * 0.0008);
      const strokeOpacity = isCurrent ? 0.40 : Math.max(0.06, 0.18 - tp.minutes_ahead * 0.002);

      features.push({
        type: 'Feature',
        properties: {
          type: 'swath',
          fill_opacity: fillOpacity,
          stroke_opacity: strokeOpacity,
          minutes_ahead: tp.minutes_ahead,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [makeCirclePolygon(tp.lat, tp.lon, radius)],
        },
      });
    });

    // Dotted center track
    if (track_points.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { type: 'track-line' },
        geometry: {
          type: 'LineString',
          coordinates: track_points.map(tp => [tp.lon, tp.lat]),
        },
      });
    }

    // Cell marker label — offset away from direction of travel
    const cell = track_points[0];
    const infoParts: string[] = [];

    const iw = stormData.impact_window_min;
    if (iw && iw[0] > 0) {
      const exitLabel = iw[1] >= 60 ? '60m+' : `${iw[1]}m`;
      infoParts.push(iw[1] > iw[0] ? `Impact ${iw[0]}-${exitLabel}` : `Impact ${iw[0]}m`);
    } else if (stormData.rain_at_circuit) {
      infoParts.push('At circuit');
    } else if (!stormData.will_intercept && stormData.closest_approach_km != null) {
      infoParts.push(`Miss ${stormData.closest_approach_km} km`);
    }

    if (stormData.display_rain_rate_mmh) infoParts.push(`${stormData.display_rain_rate_mmh} mm/h`);
    if (stormData.speed_kmh) infoParts.push(`${Math.round(stormData.speed_kmh)} km/h`);

    const offset = labelOffset(stormData.direction_deg);

    features.push({
      type: 'Feature',
      properties: {
        type: 'cell-marker',
        label: infoParts.join(' · '),
        offset_x: offset[0],
        offset_y: offset[1],
      },
      geometry: { type: 'Point', coordinates: [cell.lon, cell.lat] },
    });

    // Projected dots with time labels
    track_points.slice(1).forEach((tp) => {
      features.push({
        type: 'Feature',
        properties: {
          type: 'track-point',
          label: `+${tp.minutes_ahead}m`,
          minutes_ahead: tp.minutes_ahead,
        },
        geometry: { type: 'Point', coordinates: [tp.lon, tp.lat] },
      });
    });

    return { type: 'FeatureCollection', features };
  }, []);

  const updateLayers = useCallback((
    map: mapboxgl.Map,
    stormData: StormTrackingData,
  ) => {
    if (!isMapStyleLoaded(map)) return;

    try {
      const geojson = buildGeoJSON(stormData);

      const existingSource = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;
      if (existingSource) {
        existingSource.setData(geojson);
        return;
      }

      map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });

      // 1a. Swath — soft fill
      map.addLayer({
        id: SWATH_FILL_LAYER_ID,
        type: 'fill',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'swath'],
        paint: {
          'fill-color': '#F57C00',
          'fill-opacity': ['get', 'fill_opacity'],
        },
      });

      // 1b. Swath — outline rings (current ring bolder, future thinner)
      map.addLayer({
        id: SWATH_OUTLINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'swath'],
        paint: {
          'line-color': '#F57C00',
          'line-width': ['case',
            ['==', ['get', 'minutes_ahead'], 0], 1.5,
            1,
          ],
          'line-opacity': ['get', 'stroke_opacity'],
          'line-dasharray': [4, 3],
        },
      });

      // 2. Dotted center track
      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'track-line'],
        paint: {
          'line-color': '#E65100',
          'line-width': 2,
          'line-opacity': 0.5,
          'line-dasharray': [2, 3],
        },
      });

      // 3. Point markers (cell + projected)
      map.addLayer({
        id: POINTS_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['any',
          ['==', ['get', 'type'], 'cell-marker'],
          ['==', ['get', 'type'], 'track-point'],
        ],
        paint: {
          'circle-radius': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 8,
            4,
          ],
          'circle-color': ['case',
            ['==', ['get', 'type'], 'cell-marker'], '#F57C00',
            '#FF9800',
          ],
          'circle-stroke-width': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 2.5,
            1.5,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 1.0,
            0.7,
          ],
        },
      });

      // 4. Labels — cell marker offset away from storm, projected points below
      map.addLayer({
        id: LABELS_LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['any',
          ['==', ['get', 'type'], 'cell-marker'],
          ['==', ['get', 'type'], 'track-point'],
        ],
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 12,
            10,
          ],
          'text-offset': ['case',
            ['==', ['get', 'type'], 'cell-marker'],
            ['literal', [0, -2.2]],
            ['literal', [0, 1.5]],
          ],
          'text-anchor': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 'bottom' as const,
            'top' as const,
          ],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': ['case',
            ['==', ['get', 'type'], 'cell-marker'], '#E65100',
            '#795548',
          ],
          'text-halo-color': '#FFFFFF',
          'text-halo-width': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 3,
            1.5,
          ],
          'text-halo-blur': ['case',
            ['==', ['get', 'type'], 'cell-marker'], 0.5,
            0,
          ],
        },
      });
    } catch (e) {
      console.warn('[StormTrackingLayer] Failed to update layers:', e);
    }
  }, [isMapStyleLoaded, buildGeoJSON]);

  // Fetch storm tracking data when date range changes
  useEffect(() => {
    if (!isEnabled || !selectedDateRange) {
      setStormTrackingRecords([]);
      lastFetchedRangeRef.current = null;
      return;
    }

    const startTs = dayjs(selectedDateRange.start).utc().format('YYYYMMDDHHmmss');
    const endTs = dayjs(selectedDateRange.end).utc().format('YYYYMMDDHHmmss');
    const rangeKey = `${startTs}-${endTs}`;

    if (lastFetchedRangeRef.current === rangeKey) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const records = await stormTrackingService.getStormTrackingRange({
          start_timestamp: startTs,
          end_timestamp: endTs,
        });
        if (cancelled) return;
        setStormTrackingRecords(records);
        lastFetchedRangeRef.current = rangeKey;
      } catch (error) {
        console.error('[StormTrackingLayer] Fetch failed:', error);
        setStormTrackingRecords([]);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isEnabled, selectedDateRange]);

  // Find the record closest to the current playback timestamp
  const currentStormData = useMemo(() => {
    if (stormTrackingRecords.length === 0 || !currentTimestampUtc) return null;

    const currentTs = currentTimestampUtc;

    let closest: StormTrackingData | null = null;
    let closestDiff = Infinity;

    for (const record of stormTrackingRecords) {
      const recordTs = dayjs(record.timestamp).utc().format('YYYYMMDDHHmmss');
      if (recordTs <= currentTs) {
        const diff = parseInt(currentTs, 10) - parseInt(recordTs, 10);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = record;
        }
      }
    }

    return closest;
  }, [stormTrackingRecords, currentTimestampUtc]);

  // Update map visualization
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !isEnabled) {
      if (map && isMapStyleLoaded(map)) cleanupLayers(map);
      onStormTrackingUpdate?.(null);
      return;
    }

    onStormTrackingUpdate?.(currentStormData);

    let cancelled = false;

    const update = () => {
      if (cancelled) return;
      if (!isMapStyleLoaded(map)) {
        setTimeout(update, 100);
        return;
      }

      if (currentStormData && currentStormData.rain_detected && currentStormData.track_points.length > 0) {
        updateLayers(map, currentStormData);
      } else {
        cleanupLayers(map);
      }
    };

    update();
    return () => { cancelled = true; };
  }, [
    mapRef,
    isEnabled,
    currentStormData,
    styleVersion,
    cleanupLayers,
    updateLayers,
    onStormTrackingUpdate,
    isMapStyleLoaded,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    const map = mapRef.current;
    return () => {
      if (map && isMapStyleLoaded(map)) cleanupLayers(map);
    };
  }, [mapRef, cleanupLayers, isMapStyleLoaded]);
}
